import type { FC, MouseEvent } from 'react'
import { useState } from 'react'

import { Popup, LoaderCircle, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Image } from '@skytech/ui/Image'
import { useQuery } from '@tanstack/react-query'
import HeadphonesIcon from 'mdi-react/HeadphonesIcon'
import InformationCircleIcon from 'mdi-react/InformationCircleIcon'
import UnfoldMoreHorizontalIcon from 'mdi-react/UnfoldMoreHorizontalIcon'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'
import type { ProductGroupDataExtended } from '@/modules/ecom-policy/types'
import { imagePlaceholder } from '@/util/misc'

import { usePolicyId, useUpdateProduct } from '../hooks'
import { usePolicyFilters } from '../Policy.context'

import ProductGroupOverview from './ProductGroupOverview'
import { ProductSettingsPopup } from './ProductSettings'

const ProductGroup: FC<ProductGroupDataExtended> = ({
  groupedProducts,
  imageUrl,
  description,
  priceFrom,
  groupName,
  groupKey,
  category,
  currency,
  vatPct,
  inStock,
  isSelected,
  isExpired,
  isMandatory,
  hasAccessories,
}) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()

  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false)
  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false)

  const { updateProduct, isUpdating } = useUpdateProduct()
  const policyId = usePolicyId()
  const filters = usePolicyFilters()
  const [searchQuery] = filters.searchQuery
  const [, setCurrentlyBrowsedGroupKey] = filters.currentlyBrowsedGroupKey
  const [isVatIncluded] = filters.isVatIncluded

  const setProductsWithVisibleAccessoriesIds = filters.setProductsWithVisibleAccessoriesIds

  const selectedProductsNumber = groupedProducts.filter(({ isSelected }) => isSelected).length
  const isSingleProductGroup = groupedProducts.length === 1
  const isFromPriceTextVisible = isSingleProductGroup || selectedProductsNumber !== 1
  const product = groupedProducts[0]

  const { data } = useQuery(queries.policyProducts(policyId), () =>
    ecomPolicyResources.getPolicyProducts(policyId)
  )

  if (!data?.products) return null

  const handleGroupClick = () => {
    if (!isSingleProductGroup) {
      setIsProductsModalOpen(true)
      return
    }

    if (isSelected) {
      setIsSettingsPopupOpen(true)
      return
    }

    updateProduct({
      policyId,
      payload: {
        products: [
          {
            productId: product.id,
            isSelected: !product.isSelected,
            priceId: product.priceId,
            isMandatory,
            quantityLimit: product.quantityLimit,
            alias: product.alias,
          },
        ],
      },
    })
  }

  const handleOnlySelectedProductAccessoriesClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()

    const selectedGroupedProductsIds = groupedProducts.map(({ id }) => id)
    setProductsWithVisibleAccessoriesIds(selectedGroupedProductsIds)
    setCurrentlyBrowsedGroupKey(groupKey)
  }

  const isIconsVisible =
    isExpired || Boolean(isSelected) || (isSingleProductGroup && hasAccessories)

  return (
    <button
      className={`ecom-policy__products__product ${whiteLabel} ${isUpdating ? 'is-saving' : ''} ${
        isSelected ? 'is-selected' : ''
      } ${isExpired ? 'is-expired' : ''} ${hasAccessories ? 'has-accessories' : ''} ${
        isMandatory ? 'is-mandatory' : ''
      }`}
      disabled={isUpdating}
      onClick={handleGroupClick}
    >
      {isIconsVisible && (
        <div className="status-icons">
          {Boolean(isSelected) && isSingleProductGroup && (
            <>
              {Boolean(product.quantityLimit > 1) && (
                <div className="mt-1" id={`quantity-selector-enabled-${groupKey}`}>
                  <UnfoldMoreHorizontalIcon />

                  <Tooltip
                    anchorId={`quantity-selector-enabled-${groupKey}`}
                    content={translate('Quantity selector enabled')}
                  />
                </div>
              )}
            </>
          )}

          {isSingleProductGroup && hasAccessories && (
            <Button
              id={`has-accessories-${groupKey}`}
              size="extraSmall"
              variant="primary"
              Icon={<HeadphonesIcon />}
              className="see-accessories-button m-0 px-1"
              onClick={handleOnlySelectedProductAccessoriesClick}
            >
              <Tooltip
                anchorId={`has-accessories-${groupKey}`}
                content={translate('See product accessories')}
                size="large"
              />
            </Button>
          )}

          {isExpired && (
            <div className="expired-info" id={`expired-info-${groupKey}`}>
              <InformationCircleIcon />

              <Tooltip
                anchorId={`expired-info-${groupKey}`}
                content={translate(
                  isSingleProductGroup
                    ? 'Produktet er utgått hos leverandør'
                    : 'Gruppen inneholder produkt som er utgått hos leverandør'
                )}
              />
            </div>
          )}
        </div>
      )}

      <div>
        <div className={`ecom-policy__products__product__name ${isExpired ? 'is-expired' : ''}`}>
          <Highlight pattern={searchQuery} text={groupName} />
        </div>

        {!isSingleProductGroup && (
          <div className="category">
            <Highlight pattern={searchQuery} text={category} />
          </div>
        )}
      </div>

      <Image src={imageUrl || 'missing'} alt="product" onError={imagePlaceholder} />

      <div>
        <div className="ecom-policy__products__product__price">
          {isSingleProductGroup ? (
            <FormattedNumberWithCurrency
              amount={isVatIncluded ? priceFrom * (1 + product.vatPct / 100) : priceFrom}
              currency={currency}
            />
          ) : (
            <>
              {isFromPriceTextVisible && (
                <>
                  <Translation id="from" />{' '}
                </>
              )}
              <FormattedNumberWithCurrency
                amount={isVatIncluded ? priceFrom * (1 + vatPct / 100) : priceFrom}
                currency={currency}
              />
            </>
          )}
        </div>

        <div className="ecom-policy__products__product__stock">
          {inStock} <Translation id="in stock" />
        </div>
      </div>

      {isUpdating && (
        <div className="ecom-policy__products__product__loader">
          <LoaderCircle />
        </div>
      )}

      <ProductSettingsPopup
        isOpen={isSettingsPopupOpen}
        alias={product.alias}
        name={product.name}
        policyId={policyId}
        priceId={product.priceId}
        productId={product.id}
        isMandatory={isMandatory}
        quantityLimit={product.quantityLimit}
        onClose={() => setIsSettingsPopupOpen(false)}
      />

      <Popup
        variant="wide"
        isOpen={isProductsModalOpen}
        ariaLabel={translate('Modal with products')}
        onClose={() => setIsProductsModalOpen(false)}
      >
        <ProductGroupOverview
          groupKey={groupKey}
          groupName={groupName}
          groupedProducts={groupedProducts}
          description={description}
          hasAccessories={hasAccessories}
          onModalClose={() => setIsProductsModalOpen(false)}
        />
      </Popup>
    </button>
  )
}

export default ProductGroup
